/**
 * Authentication API functions using generated-types
 */

import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { V1Api } from '../../../generated-types/apis/V1Api'
import { Configuration } from '../../../generated-types/runtime'
import LogTool from '../../../logger/logTools'

const log = new LogTool({ context: 'AuthAPI', enable: true, logLevel: 'warn' })

export interface TokenResponse {
  access: string
  refresh: string
}

export interface GuestTokenRequest {
  guest_id: string
  password: string
}

/**
 * Get API configuration for authentication endpoints
 * Note: Auth endpoints don't require authentication tokens
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    // No access token needed for auth endpoints
    accessToken: () => ''
  })
}

/**
 * Create V1Api instance for authentication
 */
const v1Api = new V1Api(getApiConfiguration())

/**
 * Obtain guest token using guest_id and password
 * The backend endpoint accepts {guest_id, password} and returns {access, refresh} tokens
 */
export async function guestTokenApi(guestId: string, password: string): Promise<TokenResponse> {
  log.info('Obtaining guest token for guest_id:', guestId)

  // The backend accepts {guest_id, password} at /v1/auth/token/
  // Since V1Api.v1AuthTokenCreate expects APIOrgUserTokenObtainPairRequest (with email),
  // we use the raw method and manually construct the request body
  const requestBody = {
    guest_id: guestId,
    password: password,
  }

  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }

  const baseUrl = envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl
  const url = `${baseUrl}/v1/auth/token/`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })

  if (!response.ok) {
    let errorData
    try {
      errorData = await response.json()
    } catch {
      errorData = await response.text()
    }
    log.error('Guest token request failed:', errorData)
    throw new Error(`Guest token request failed: ${response.status} ${response.statusText}`)
  }

  const data = await response.json() as TokenResponse

  if (!data.access || !data.refresh) {
    throw new Error('Invalid token response: missing access or refresh token')
  }

  // Store tokens in cookies with proper expiration
  const accessDecoded = jwtDecode(data.access)
  const refreshDecoded = jwtDecode(data.refresh)

  Cookies.set('access', data.access, {
    expires: new Date((accessDecoded.exp as number) * 1000),
  })
  Cookies.set('refresh', data.refresh, {
    expires: new Date((refreshDecoded.exp as number) * 1000),
  })

  log.info('Guest token obtained successfully')
  return data
}

